import { SCHEMA } from '@vedalib/editor/lib/brand'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { useCallback, useMemo } from 'react'
import React from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldFileId,
  FieldNumber,
  FieldNumberArray,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import { OFFSET_ARRAY } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { TestMatchingFormBrandType } from './testMatchingTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.testMatching.style, {
  icon: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.testMatching.schema.dragIcon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
  },
  pair: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 1, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
  },
  border: {
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  mobileIcon: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.testMatching.schema.jumpIcon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
  },
  selected: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.testMatching.schema.selectedBg'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.testMatching.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
  indents: {
    padding: genBrandField({
      as: FieldNumberArray,
      label: t('input.label.innerAnswerIndents'),
      layout: 'vertical',
      props: { options: OFFSET_ARRAY },
    }),
    marginBottom: genBrandField({
      as: FieldNumber,
      label: t('input.label.answerIndents'),
      props: { width: 64 },
    }),
    marginRight: genBrandField({
      as: FieldNumber,
      label: t('input.label.iconIndents'),
      props: { width: 64 },
    }),
  },
})

const TestMatchingFormStyle: TestMatchingFormBrandType = ({
  device,
  subConfigs,
  setForm,
  edited,
}) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  const handleSetFeedback = useCallback(() => setForm?.(BrandFormEnum.FEEDBACK), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom bottomSpace gap sides>
        <Typography weight='medium'>{t('elements.testMatching.tags.answer')}</Typography>
        <FormField config={schema.icon.backgroundImage} />
        <FormField config={schema.pair.backgroundColor} />
        <FormField config={schema.pair.borderRadius} />
        <FormField config={schema.border.borderColor} />
        <FormField config={schema.border.borderWidth} />
        <FormField config={schema.hover.backgroundColor} />

        <Typography weight='medium'>{t('elements.testMatching.tags.answerSelection')}</Typography>
        <FormField config={schema.mobileIcon.backgroundImage} />
        <FormField config={schema.selected.backgroundColor} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />

        <Typography weight='medium'>{t('elements.testMatching.tags.indents')}</Typography>
        <FormField config={schema.indents.padding} />
        <FormField config={schema.indents.marginBottom} />
        <FormField config={schema.indents.marginRight} />

        <TileButton
          description={t('uiKit.button.feedbackDescription')}
          icon='otherRight'
          name='showFeedbackForm'
          onClick={handleSetFeedback}
        >
          <Typography weight='medium'>{t('page.brand.tab.feedback')}</Typography>
          {isAnyEdited(edited.feedback) && <BrandTagEdited />}
        </TileButton>

        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
          {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(TestMatchingFormStyle) as typeof TestMatchingFormStyle
